import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { attach, attachCancel, evolve, evolveCancel } from '../Pokemon/actions';
import { selectAttachFlowing, selectAttaching, selectEvolveFlowing, selectEvolving } from '../Pokemon/selectors';
import { selectEvolveTargets } from '../Board/selectors';
import { selectActivePokemon, selectIsActivePlayer } from '../ActiveGame/selectors';
import ActionableButton from '../ActionableButton';
import CardFace from '../Card/CardFace';
import { Slot } from '../Card/styled';
import { ACTIVE } from '../ActiveGame/contextMenuTypes';
import { setContextMenu } from '../ActiveGame/actions';
import { conversionBadges } from '../Card/conversions';
import usePickTarget from '../Picks/usePickTarget';

const ActivePokemon = ({ userId }) => {
  const dispatch = useDispatch();
  const attachFlowing = useSelector(selectAttachFlowing);
  const attaching = useSelector(selectAttaching);
  const evolveFlowing = useSelector(selectEvolveFlowing);
  const evolveTargets = useSelector(selectEvolveTargets);
  const evolving = useSelector(selectEvolving);
  const activePokemon = useSelector((state) => selectActivePokemon(state, { userId }));
  const isActivePlayer = useSelector((state) => selectIsActivePlayer(state, { userId }));
  const pickTarget = usePickTarget(
    userId,
    'active',
    activePokemon ? activePokemon.getIn(['card', 'name'], 'this Pokémon') : '',
    activePokemon ? activePokemon.get('energyAttachments') : undefined,
  );

  if (!activePokemon) {
    return <Slot aria-label="No active Pokémon" />;
  }

  let selectActiveAction = () => dispatch(setContextMenu(ACTIVE, userId));
  let onActiveCancel = null;

  const name = activePokemon.getIn(['card', 'name'], 'this Pokémon');
  let prompt = null;
  let busy = false;

  if (isActivePlayer && attachFlowing) {
    selectActiveAction = () => dispatch(attach());
    onActiveCancel = () => dispatch(attachCancel());
    prompt = `Attach the energy to ${name}?`;
    busy = attaching;
  }

  // Only a Pokémon the server says the card can evolve lights up.
  if (isActivePlayer && evolveFlowing && evolveTargets.includes('active')) {
    selectActiveAction = () => dispatch(evolve());
    onActiveCancel = () => dispatch(evolveCancel());
    prompt = `Evolve ${name}?`;
    busy = evolving;
  }

  // A Trainer or Power can pick either player's Pokémon; the hook knows which.
  if (pickTarget) {
    selectActiveAction = pickTarget.onClick;
    onActiveCancel = pickTarget.onCancel;
    prompt = pickTarget.prompt;
    busy = pickTarget.busy;
  }

  return (
    <ActionableButton
      onClick={selectActiveAction}
      onCancel={onActiveCancel}
      disabled={busy}
      prompt={prompt}
      plain
    >
      <CardFace
        card={activePokemon.get('card')}
        damage={activePokemon.get('damage')}
        conditions={activePokemon.get('conditions')}
        protection={activePokemon.get('protection')}
        hindrance={activePokemon.get('hindrance')}
        hinderedBy={activePokemon.get('hindered_by')}
        trainers={activePokemon.get('trainers')}
        conversions={conversionBadges(activePokemon)}
        energyAttachments={activePokemon.get('energyAttachments')}
        targetable={!!onActiveCancel && !busy}
      />
    </ActionableButton>
  );
};

ActivePokemon.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default ActivePokemon;
